import { useEffect, useState } from 'react';
import { getHqCatalog, previewHqRole, saveHqRole, retireHqRole, checkHqRequest, HqCatalog, HqRoleDef, HqRolePreview } from '../../api/client';
import { PROTECTED_TEXT, WRITES_CLOSED_TEXT } from './hqPermissionsModel';
import { refreshHqAccess } from './hqAccess';
import { getUnresolved, recordAttempt, markUnknown, clearAttempt, classifyHqResponse, classifyHqCheck, NO_RECORD_YET_TEXT, lastRecordFailure, notStartedText } from './unresolvedChange';
import { useAttemptStore } from './useAttemptStore';

/** the exact request of an attempt whose answer was lost: re-sent as-is (same id, same payload), never rebuilt from the screen */
type RolesOp = { kind: 'save'; body: { roleId: string | null; label: string; description: string; keys: string[]; expectedVersion: number } } | { kind: 'retire'; roleId: string; version: number };
const UNRESOLVED_KEY = 'roles-panel';

/**
 * BUILD-039 — Settings › User Management › Roles. The role list with its description and who holds it, plus the role lifecycle:
 * Add custom role, Rename custom role (display name / description only: same role, same permissions, same people, same history) and
 * Retire custom role. Built-in roles keep their name and cannot be retired. The SERVER decides every change: it refuses to retire a role
 * anyone still holds (including inactive people and people whose Unique HQ access is off) and never assigns a retired role. Nobody is
 * reassigned automatically — the people listed are moved from their account in the Directory. What a role can do is edited in Permissions.
 */
type Status = { kind: 'ok' | 'error' | 'unknown'; text: string; requestId?: string; members?: string[] };
export const PERMISSIONS_ROLE_KEY = 'settings.permissionsRole';

export function RolesPanel({ nameOf, onEditPermissions }: { nameOf: (employeeId: string) => string; onEditPermissions: () => void }) {
  const [catalog, setCatalog] = useState<HqCatalog | null>(null);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);
  const [adding, setAdding] = useState<{ label: string; description: string; requestId: string } | null>(null);
  const [renaming, setRenaming] = useState<{ roleId: string; label: string; description: string; requestId: string } | null>(null);
  const [members, setMembers] = useState<Record<string, HqRolePreview | 'loading' | 'error'>>({});
  useAttemptStore();

  async function load() {
    const r = await getHqCatalog().catch(() => null);
    if (!r) { setLoadError('Roles could not be loaded. Try again.'); return; }
    if (!r.ok) { setLoadError(r.status === 403 ? 'You do not have permission to see Unique HQ roles.' : (r.body?.message || 'Roles could not be loaded.')); return; }
    setLoadError(''); setCatalog(r.body);
  }
  useEffect(() => { load(); }, []);

  if (loadError) return <div className="p-4 text-sm text-red-700" role="alert">{loadError} <button className="underline" onClick={load}>Retry</button></div>;
  if (!catalog) return <div className="p-4 text-sm text-gray-500" role="status">Loading roles…</div>;
  const writesClosed = catalog.writes?.open === false;
  const canChange = catalog.viewer.canChange !== false && !writesClosed;
  const assignedTo = (roleId: string) => catalog.assignments.filter((a) => a.roleId === roleId).map((a) => a.employeeId);

  /** a change whose answer was lost: say so, and offer the check (sending the same request again is also safe) */
  // "unresolved" = the answer does not prove the attempt settled (lost, generic error, 429, malformed, or an early refusal of a RETRY); see classifyHqResponse
  const unresolved = getUnresolved<RolesOp>(UNRESOLVED_KEY);   // while set, nothing else can be started: it stays unknown until a definite answer
  const sender = (requestId: string, op: RolesOp) => (op.kind === 'save' ? saveHqRole(requestId, op.body) : retireHqRole(requestId, op.roleId, op.version));
  /**
   * Record the attempt in durable browser storage BEFORE sending, send it, and settle it only on an answer that proves it (see classifyHqResponse).
   * Returns the settling response, or null when the attempt stays unresolved (or could not be started: nothing is sent then).
   */
  async function runAttempt(requestId: string, op: RolesOp, label: string, what: string): Promise<any | null> {
    if (!recordAttempt(UNRESOLVED_KEY, { requestId, payload: op, label })) { setStatus({ kind: 'error', text: notStartedText(lastRecordFailure()) }); return null; }
    setBusy(true); setStatus(null);
    const r = await sender(requestId, op).catch(() => null);
    setBusy(false);
    if (!r || classifyHqResponse(r, false) === 'unresolved') {
      markUnknown(UNRESOLVED_KEY, requestId);
      setStatus({ kind: 'unknown', text: `The answer did not settle it, so it is not known whether the role was ${what}. It stays unresolved: use "Check", or "Save again (same change)", which is applied once.` });
      return null;
    }
    clearAttempt(UNRESOLVED_KEY, requestId);
    return r;
  }

  async function create() {
    if (unresolved || !adding || !adding.label.trim()) return;
    const body = { roleId: null, label: adding.label.trim(), description: adding.description.trim(), keys: [] as string[], expectedVersion: 0 };
    const r = await runAttempt(adding.requestId, { kind: 'save', body }, `creating the role "${body.label}"`, 'created');
    if (!r) return;
    if (!r!.ok) { setStatus({ kind: 'error', text: r!.body?.message || 'The role was not created.' }); return; }
    setStatus({ kind: 'ok', text: r!.body.replayed ? 'This role was already created.' : `Created "${adding.label.trim()}". It has no permissions yet — set them in Permissions, then give it to people in the Directory.` });
    try { sessionStorage.setItem(PERMISSIONS_ROLE_KEY, r!.body.roleId); } catch { /* ignore */ }
    setAdding(null); refreshHqAccess(); await load();
  }
  async function rename(role: HqRoleDef) {
    if (unresolved || !renaming || !renaming.label.trim()) return;
    // same permissions, same version: the server changes the name / description only (role id, permissions, people and history are kept)
    const body = { roleId: role.roleId, label: renaming.label.trim(), description: renaming.description, keys: [...role.permissions], expectedVersion: role.version };
    const r = await runAttempt(renaming.requestId, { kind: 'save', body }, `renaming the role "${role.label}"`, 'renamed');
    if (!r) return;
    if (!r!.ok) {
      const conflict = r!.body?.reason === 'version-conflict';
      setStatus({ kind: 'error', text: conflict ? 'Someone else changed this role since you opened it. Nothing was renamed; the latest version is shown now.' : (r!.body?.message || 'The role was not renamed.') });
      if (conflict) { setRenaming(null); await load(); }
      return;
    }
    setStatus({ kind: 'ok', text: r!.body.replayed ? 'This rename was already saved.' : `Renamed "${role.label}" to "${renaming.label.trim()}". Its permissions and the people who hold it are unchanged.` });
    setRenaming(null); refreshHqAccess(); await load();
  }
  async function retire(role: HqRoleDef) {
    if (unresolved) return;
    if (!window.confirm(`Retire the role "${role.label}"? It can no longer be given to anyone. Its history is kept.`)) return;
    const requestId = crypto.randomUUID();
    const r = await runAttempt(requestId, { kind: 'retire', roleId: role.roleId, version: role.version }, `retiring the role "${role.label}"`, 'retired');
    if (!r) return;
    if (!r!.ok) {
      if (r!.body?.reason === 'role-has-members') {
        await load();
        setStatus({ kind: 'error', text: `"${role.label}" was not retired: people still hold it. Give each of them another role from their account in the Directory, then retire it.`, members: assignedTo(role.roleId).map(nameOf) });
        return;
      }
      setStatus({ kind: 'error', text: r!.body?.message || 'The role was not retired.' }); return;
    }
    setStatus({ kind: 'ok', text: r!.body.replayed ? 'This role was already retired.' : `Retired "${role.label}". It is kept in the retired list for history.` });
    refreshHqAccess(); await load();
  }
  async function check() {
    if (!unresolved) return;
    const r = await checkHqRequest(unresolved.requestId).catch(() => null);
    if (!r || !r.ok) { setStatus({ kind: 'unknown', text: 'Still not known. Try Check again in a moment.' }); return; }
    const v = classifyHqCheck(r);
    if (v === 'applied') { clearAttempt(UNRESOLVED_KEY, unresolved.requestId); setStatus({ kind: 'ok', text: 'Checked: the change WAS saved.' }); setAdding(null); setRenaming(null); refreshHqAccess(); await load(); }
    else if (v === 'refused') { clearAttempt(UNRESOLVED_KEY, unresolved.requestId); setStatus({ kind: 'error', text: 'Checked: the platform refused this change and recorded the refusal, so it can never be applied. Nothing was changed.' }); setAdding(null); setRenaming(null); await load(); }
    else setStatus({ kind: 'unknown', text: NO_RECORD_YET_TEXT });          // an absent record is NOT "not saved": the first attempt may still finish
  }
  /** send the lost attempt again: the same request id and the same payload (never what is on the screen now) */
  async function again() {
    const u = unresolved; if (!u) return;
    setBusy(true); setStatus(null);
    const r = await sender(u.requestId, u.payload).catch(() => null);
    setBusy(false);
    if (!r || classifyHqResponse(r, true) === 'unresolved') { markUnknown(UNRESOLVED_KEY, u.requestId); setStatus({ kind: 'unknown', text: 'That did not settle it, so it is still not known. It stays unresolved with the same request.' }); return; }
    clearAttempt(UNRESOLVED_KEY, u.requestId);                               // a recorded outcome or a recorded (fenced) refusal settles the attempt
    if (!r!.ok) { setStatus({ kind: 'error', text: r!.body?.message || 'The change was refused. Nothing was changed.' }); return; }
    setStatus({ kind: 'ok', text: r!.body.replayed ? 'This change was already saved.' : 'The change was saved.' });
    setAdding(null); setRenaming(null); refreshHqAccess(); await load();
  }
  async function showMembers(role: HqRoleDef) {
    setMembers((m) => ({ ...m, [role.roleId]: 'loading' }));
    // read only: the same server read the Permissions review uses, so people who hold a built-in role through their sign-in group are included
    const r = await previewHqRole(role.roleId, [...role.permissions]).catch(() => null);
    setMembers((m) => ({ ...m, [role.roleId]: r && r.ok ? r.body : 'error' }));
  }
  function editPermissions(role: HqRoleDef) {
    try { sessionStorage.setItem(PERMISSIONS_ROLE_KEY, role.roleId); } catch { /* ignore */ }
    onEditPermissions();
  }

  const statusClass = status?.kind === 'ok' ? 'bg-green-50 text-green-700' : status?.kind === 'unknown' ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700';
  const retired = catalog.retiredRoles || [];
  return (
    <div className="p-4 max-w-4xl" data-testid="roles-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <p className="text-xs text-gray-600 max-w-2xl">
          The Unique HQ roles people can be given. Built-in roles match today's sign-in groups and keep their names. What each role can do is set in
          Permissions; who has which role is set on each person in the Directory.
        </p>
        {canChange && !adding && (
          <button onClick={() => { setStatus(null); setAdding({ label: '', description: '', requestId: crypto.randomUUID() }); }} disabled={busy || !!unresolved}
            className="px-3 py-1.5 bg-blue text-white rounded text-xs font-medium whitespace-nowrap" data-testid="add-role">+ Add custom role</button>
        )}
      </div>
      {writesClosed && <p className="mb-3 text-xs text-amber-800 bg-amber-50 rounded px-3 py-2" role="status" data-testid="writes-closed">{WRITES_CLOSED_TEXT}</p>}
      {!canChange && !writesClosed && <p className="mb-3 text-xs text-gray-600" data-testid="roles-read-only">{PROTECTED_TEXT.readOnlyViewer}</p>}

      {status && (
        <div role={status.kind === 'ok' ? 'status' : 'alert'} className={`mb-3 text-sm rounded px-3 py-2 ${statusClass}`} data-testid="roles-status">
          {status.text}
          {status.members && status.members.length > 0 && (
            <ul className="list-disc pl-5 mt-1" data-testid="roles-status-members">{status.members.map((n, i) => <li key={i}>{n}</li>)}</ul>
          )}
        </div>
      )}
      {unresolved && (
        <div role="alert" className="mb-3 text-sm rounded px-3 py-2 bg-amber-50 text-amber-800" data-testid="roles-unresolved">
          An earlier attempt at {unresolved.label} has an unknown result. Nothing else can be changed here until it is settled.
          <button className="ml-2 underline" disabled={busy} onClick={check} data-testid="roles-check">Check</button>
          <button className="ml-2 underline" disabled={busy} onClick={again} data-testid="roles-save-again">Save again (same change)</button>
        </div>
      )}

      {adding && (
        <section className="mb-4 border border-blue/40 rounded-lg p-4 bg-blue/5" data-testid="add-role-form" aria-label="Add custom role">
          <h3 className="text-base font-bold text-navy mb-2">New custom role</h3>
          <div className="flex flex-wrap gap-3">
            <label className="text-xs text-gray-600">Role name
              <input aria-label="Role name" className="block mt-1 px-2 py-1 border rounded text-sm w-56" maxLength={60} value={adding.label} onChange={(e) => setAdding({ ...adding, label: e.target.value })} />
            </label>
            <label className="text-xs text-gray-600 flex-1 min-w-[16rem]">Description
              <input aria-label="Role description" className="block mt-1 px-2 py-1 border rounded text-sm w-full" maxLength={300} value={adding.description} onChange={(e) => setAdding({ ...adding, description: e.target.value })} />
            </label>
          </div>
          <p className="mt-2 text-xs text-gray-600">It starts with no permissions; you set them in Permissions.</p>
          <div className="mt-3 flex gap-2">
            <button onClick={create} disabled={busy || !!unresolved || !adding.label.trim()} className="px-3 py-1.5 bg-blue text-white rounded text-xs font-medium disabled:opacity-50" data-testid="create-role">Create role</button>
            <button onClick={() => setAdding(null)} disabled={busy} className="px-3 py-1.5 border rounded text-xs">Cancel</button>
          </div>
        </section>
      )}

      <div className="space-y-3" data-testid="role-list">
        {catalog.roles.map((role) => {
          const assigned = assignedTo(role.roleId);
          const isRenaming = renaming?.roleId === role.roleId;
          const m = members[role.roleId];
          return (
            <section key={role.roleId} className="border border-gray-300 rounded-lg bg-white shadow-sm" data-testid={`role-card-${role.code}`} aria-labelledby={`role-${role.roleId}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3 bg-gray-50 border-b border-gray-300 rounded-t-lg">
                <div className="min-w-0">
                  <h3 id={`role-${role.roleId}`} className="text-base font-bold text-navy">
                    {role.label}
                    <span className="ml-2 align-middle text-[11px] font-medium px-1.5 py-0.5 rounded bg-gray-200 text-gray-700">{role.isSystem ? 'Built-in' : 'Custom'}</span>
                  </h3>
                  {role.description && <p className="text-xs text-gray-600 mt-0.5">{role.description}</p>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => editPermissions(role)} className="px-2.5 py-1 border rounded text-xs text-blue" data-testid={`role-permissions-${role.code}`}>Permissions</button>
                  {canChange && !role.isSystem && !isRenaming && (
                    <button onClick={() => { setStatus(null); setRenaming({ roleId: role.roleId, label: role.label, description: role.description, requestId: crypto.randomUUID() }); }}
                      disabled={busy || !!unresolved} className="px-2.5 py-1 border rounded text-xs" data-testid={`rename-role-${role.code}`}>Rename</button>
                  )}
                  {canChange && !role.isSystem && (
                    <button onClick={() => retire(role)} disabled={busy || !!unresolved} className="px-2.5 py-1 border border-red-300 rounded text-xs text-red-700" data-testid={`retire-role-${role.code}`}>Retire</button>
                  )}
                </div>
              </div>
              <div className="px-4 py-3 text-xs text-gray-700">
                {isRenaming && (
                  <div className="mb-3 flex flex-wrap items-end gap-3" data-testid={`rename-form-${role.code}`}>
                    <label className="text-xs text-gray-600">New name
                      <input aria-label="New role name" className="block mt-1 px-2 py-1 border rounded text-sm w-56" maxLength={60} value={renaming!.label} onChange={(e) => setRenaming({ ...renaming!, label: e.target.value })} />
                    </label>
                    <label className="text-xs text-gray-600 flex-1 min-w-[14rem]">Description
                      <input aria-label="New role description" className="block mt-1 px-2 py-1 border rounded text-sm w-full" maxLength={300} value={renaming!.description} onChange={(e) => setRenaming({ ...renaming!, description: e.target.value })} />
                    </label>
                    <button onClick={() => rename(role)} disabled={busy || !!unresolved || !renaming!.label.trim() || (renaming!.label.trim() === role.label && renaming!.description === role.description)}
                      className="px-3 py-1.5 bg-blue text-white rounded text-xs font-medium disabled:opacity-50" data-testid={`save-rename-${role.code}`}>Save name</button>
                    <button onClick={() => setRenaming(null)} disabled={busy} className="px-3 py-1.5 border rounded text-xs">Cancel</button>
                  </div>
                )}
                <p data-testid={`role-members-${role.code}`}>
                  {role.allPermissions ? 'Every Unique HQ permission except Vendors. ' : ''}
                  {assigned.length === 0 ? 'Not given to anyone by name.' : `Given by name to ${assigned.length} ${assigned.length === 1 ? 'person' : 'people'}: ${assigned.map(nameOf).join(', ')}.`}
                  {role.isSystem && role.legacyGroup ? ' People in the matching sign-in group hold it too, unless they were given another role.' : ''}
                  {' '}
                  <button className="text-blue underline" onClick={() => showMembers(role)} data-testid={`show-members-${role.code}`}>Show everyone who holds it</button>
                </p>
                {m === 'loading' && <p className="mt-1 text-gray-500" role="status">Loading…</p>}
                {m === 'error' && <p className="mt-1 text-red-700" role="alert">The list could not be loaded.</p>}
                {m && typeof m === 'object' && (
                  <div className="mt-2" data-testid={`members-list-${role.code}`}>
                    {!m.complete && <p className="text-red-700" role="alert">Not everyone who holds this role could be listed. This is not a complete list.</p>}
                    {m.people.length === 0 ? <p className="text-gray-600">{m.complete ? 'Nobody with a person record holds this role.' : 'Nobody could be listed.'}</p> : (
                      <ul className="list-disc pl-5">{m.people.map((p) => <li key={p.employeeId}>{nameOf(p.employeeId)}{p.via === 'sign-in-group' ? ' (through their sign-in group)' : ''}</li>)}</ul>
                    )}
                    {m.unmatchedLogins > 0 && <p className="text-gray-600">{m.unmatchedLogins} sign-in{m.unmatchedLogins === 1 ? '' : 's'} with this role {m.unmatchedLogins === 1 ? 'has' : 'have'} no person record.</p>}
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>

      {retired.length > 0 && (
        <details className="mt-4 text-xs" data-testid="retired-roles">
          <summary className="cursor-pointer font-medium text-gray-600">Retired roles ({retired.length})</summary>
          <ul className="mt-1 pl-5 list-disc text-gray-600">
            {retired.map((r) => <li key={r.roleId}>{r.label} — retired {new Date(r.retiredAt).toLocaleDateString()}. Kept for history; it cannot be given to anyone.</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}
